import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Check, Lock, Crown, BookOpen, Users, Calendar, Gift, ChevronRight, Loader2, Star, ExternalLink, Heart, Sparkles } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SKOOL_URL = "https://www.skool.com/letter-writers-society-5504";

// Tier Card Component
const TierCard = ({ tier, isCurrentTier, onSelect, userTier, loading }) => {
  const isPenFriend = tier.id === "pen_friend";
  const isCorrespondent = tier.id === "correspondent";
  const isRenewal = tier.id === "correspondent_renewal";
  const isPostmaster = tier.id === "postmaster";
  
  // Check if user has access
  const hasAccess = 
    (isPenFriend && ["pen_friend", "correspondent", "postmaster"].includes(userTier)) ||
    (isCorrespondent && ["correspondent", "postmaster"].includes(userTier)) ||
    (isRenewal && ["correspondent", "postmaster"].includes(userTier)) ||
    (isPostmaster && userTier === "postmaster");
  
  // Check if renewal is locked (needs course completion)
  const isRenewalLocked = isRenewal && !["correspondent", "postmaster", "course", "society"].includes(userTier);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative bg-white border-2 rounded-lg overflow-hidden ${
        isCurrentTier ? 'border-sage-600 shadow-lg' : 
        isCorrespondent ? 'border-amber-400' : 'border-stone-200'
      }`}
      data-testid={`tier-card-${tier.id}`}
    >
      {/* Badge */}
      {isCurrentTier && (
        <div className="absolute top-0 left-0 right-0 bg-sage-600 text-white text-center py-1 text-xs uppercase tracking-wider">
          Your Current Plan
        </div>
      )}
      {isCorrespondent && !isCurrentTier && (
        <div className="absolute top-0 left-0 right-0 bg-amber-500 text-white text-center py-1 text-xs uppercase tracking-wider flex items-center justify-center gap-1">
          <Star className="w-3 h-3" /> Most Popular
        </div>
      )}
      {isPostmaster && (
        <div className="absolute top-0 left-0 right-0 bg-purple-600 text-white text-center py-1 text-xs uppercase tracking-wider flex items-center justify-center gap-1">
          <Crown className="w-3 h-3" /> By Application
        </div>
      )}
      
      <div className={`p-6 ${(isCurrentTier || isCorrespondent || isPostmaster) ? 'pt-10' : ''}`}>
        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-serif text-stone-800 mb-1">{tier.name}</h3>
          <p className="text-stone-500 text-sm">{tier.tagline || tier.description}</p>
        </div>
        
        {/* Price */}
        <div className="text-center mb-6">
          {tier.price > 0 ? (
            <>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-serif text-sage-700">${tier.price}</span>
                <span className="text-stone-500 text-sm">
                  {tier.interval === "month" ? "/month" : 
                   tier.interval === "year" ? "/year" : 
                   tier.interval === "one_time" ? "" : ""}
                </span>
              </div>
              {isCorrespondent && tier.payment_plans && (
                <p className="text-xs text-stone-500 mt-2">
                  or 3 × $99 payment plan
                </p>
              )}
            </>
          ) : (
            <div className="text-2xl font-serif text-purple-600">Complimentary</div>
          )}
        </div>
        
        {/* Features */}
        <div className="space-y-3 mb-6">
          {tier.features?.slice(0, 6).map((feature, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-sage-600 mt-0.5 flex-shrink-0" />
              <span className="text-stone-700">{feature}</span>
            </div>
          ))}
          
          {tier.withheld?.slice(0, 3).map((item, idx) => (
            <div key={idx} className="flex items-start gap-2 text-sm opacity-50">
              <Lock className="w-3 h-3 text-stone-400 mt-0.5 flex-shrink-0" />
              <span className="text-stone-400 line-through">{item}</span>
            </div>
          ))}
        </div>
        
        {/* Guarantee Badge */}
        {tier.guarantee && (
          <div className="mb-4 p-3 bg-sage-50 rounded-lg border border-sage-200">
            <p className="text-xs text-sage-700 text-center">
              <Heart className="w-3 h-3 inline mr-1" />
              {tier.guarantee.name}
            </p>
          </div>
        )}
        
        {/* CTA Button */}
        {isPostmaster ? (
          <a
            href="#hosting-application"
            className="block w-full py-3 text-center border-2 border-purple-600 text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-50 transition-colors"
            data-testid="apply-postmaster-btn"
          >
            Apply to Become a Postmaster
          </a>
        ) : isRenewalLocked ? (
          <button
            disabled
            className="w-full py-3 bg-stone-100 text-stone-400 rounded-lg text-sm font-medium cursor-not-allowed"
          >
            <Lock className="w-4 h-4 inline mr-2" />
            Complete Course First
          </button>
        ) : hasAccess ? (
          <button
            disabled
            className="w-full py-3 bg-sage-100 text-sage-700 rounded-lg text-sm font-medium cursor-default"
          >
            <Check className="w-4 h-4 inline mr-2" />
            Active
          </button>
        ) : (
          <Button
            onClick={() => onSelect(tier.id)}
            disabled={loading}
            className={`w-full py-3 text-sm font-medium ${
              isCorrespondent 
                ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                : 'bg-sage-600 hover:bg-sage-700 text-white'
            }`}
            data-testid={`tier-btn-${tier.id}`}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>Get Started</>
            )}
          </Button>
        )}
      </div>
    </motion.div>
  );
};

// Bonus Card Component
const BonusCard = ({ bonus, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    className="bg-white border border-stone-200 rounded-lg p-5"
  >
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Gift className="w-4 h-4 text-amber-600" />
      </div>
      <div>
        <h4 className="font-medium text-stone-800 mb-1">{bonus.name || bonus.title}</h4>
        <p className="text-sm text-stone-500">{bonus.description}</p>
      </div>
    </div>
  </motion.div>
);

// Main Membership Page
export function MembershipPage() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState({});
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(1);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [tiersRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/membership/tiers`),
        user ? fetch(`${API_URL}/api/membership/status`, { credentials: 'include' }) : null
      ]);
      
      const tiersData = await tiersRes.json();
      setTiers(tiersData.tiers || {});
      
      if (statusRes) {
        const statusData = await statusRes.json();
        setMembershipStatus(statusData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTier = async (tierId) => {
    if (!user) {
      window.location.hash = '#login';
      return;
    }
    
    setCheckoutLoading(tierId);
    
    try {
      const response = await fetch(`${API_URL}/api/membership/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tier_id: tierId,
          origin_url: window.location.origin,
          payment_plan: selectedPlan
        })
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const userTier = membershipStatus?.current_tier || 'free';
  const courseCompleted = membershipStatus?.course_completed;

  // Order tiers for display
  const displayTiers = ['pen_friend', 'correspondent', 'correspondent_renewal', 'postmaster']
    .map(id => tiers[id])
    .filter(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sage-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="membership-page">
      {/* Hero Section */}
      <section className="bg-lws-paper py-20 px-6 border-b border-stone-200">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-serif mb-6 text-stone-800"
          >
            The Correspondence Method
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-stone-700 mb-8 max-w-2xl mx-auto"
          >
            Reclaim Depth, Presence & Human Connection Through the Lost Art of Letter Writing
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-stone-600 text-sm max-w-xl mx-auto"
          >
            For people exhausted by shallow digital communication, quietly lonely, and craving something slower — this is how you write your first letter and begin a practice that transforms your most important relationships.
          </motion.p>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif text-stone-800 mb-4">Choose Your Path</h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Whether you're just curious or ready to fully embrace the art of correspondence, there's a place for you in the Letter Writers Society.
            </p>
          </div>

          {/* Tier Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {displayTiers.map((tier) => (
              <TierCard
                key={tier.id}
                tier={tier}
                isCurrentTier={userTier === tier.id || 
                  (tier.id === 'correspondent' && ['course', 'society', 'correspondent'].includes(userTier)) ||
                  (tier.id === 'pen_friend' && ['community', 'pen_friend'].includes(userTier))}
                onSelect={handleSelectTier}
                userTier={userTier}
                loading={checkoutLoading === tier.id}
              />
            ))}
          </div>

          {/* Payment Plan Toggle for Correspondent */}
          {tiers.correspondent && userTier === 'free' && (
            <div className="mt-8 text-center">
              <p className="text-stone-500 mb-4">For The Correspondent:</p>
              <div className="inline-flex bg-stone-100 rounded-lg p-1">
                <button
                  onClick={() => setSelectedPlan(1)}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    selectedPlan === 1 ? 'bg-white shadow text-stone-800' : 'text-stone-500'
                  }`}
                >
                  Pay in Full — $247 (Save $50)
                </button>
                <button
                  onClick={() => setSelectedPlan(3)}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    selectedPlan === 3 ? 'bg-white shadow text-stone-800' : 'text-stone-500'
                  }`}
                >
                  3 × $99 Payment Plan
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-16 px-6 bg-amber-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm mb-4">
              <Sparkles className="w-4 h-4" />
              Included with The Correspondent
            </div>
            <h2 className="text-3xl font-serif text-stone-800 mb-4">Bonus Suite</h2>
            <p className="text-stone-600">Worth $247+ — Yours free with your course purchase</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {tiers.correspondent?.bonuses?.map((bonus, idx) => (
              <BonusCard key={idx} bonus={bonus} index={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-white border-2 border-sage-200 rounded-2xl p-8 md:p-12">
            <Heart className="w-12 h-12 text-sage-600 mx-auto mb-6" />
            <h2 className="text-2xl font-serif text-stone-800 mb-4">The First Letter Guarantee</h2>
            <p className="text-stone-600 text-lg">
              Complete Modules 1–3 and write your first letter. If you don't feel something shift, we'll refund you in full.
            </p>
          </div>
        </div>
      </section>

      {/* Join Community CTA */}
      <section className="py-16 px-6 bg-sage-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-serif mb-4">Join Our Community</h2>
          <p className="text-white/80 mb-8">
            Connect with fellow letter writers in our Skool community — share your journey, get writing prompts, and find your pen pals.
          </p>
          <a
            href={SKOOL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-sage-800 px-8 py-4 rounded-lg font-medium hover:bg-sage-50 transition-colors"
          >
            Visit Our Skool Community
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}

// Success Page
export function MembershipSuccessPage() {
  const [status, setStatus] = useState('loading');
  const [tier, setTier] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1]);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      checkStatus(sessionId);
    }
  }, []);

  const checkStatus = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/membership/checkout/status/${sessionId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setTier(data.tier_id);
      } else {
        setStatus('pending');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const tierNames = {
    pen_friend: 'The Pen Friend',
    correspondent: 'The Correspondent',
    correspondent_renewal: 'The Correspondent (Annual)',
    community: 'The Pen Friend',
    course: 'The Correspondent',
    society: 'The Correspondent (Annual)'
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full bg-white rounded-2xl shadow-lg p-8 text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-sage-600 mx-auto mb-4" />
            <h1 className="text-2xl font-serif text-stone-800">Processing...</h1>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-sage-600" />
            </div>
            <h1 className="text-3xl font-serif text-stone-800 mb-4">Welcome, Correspondent!</h1>
            <p className="text-stone-600 mb-6">
              Your {tierNames[tier] || 'membership'} is now active. The art of letter writing awaits you.
            </p>
            <div className="space-y-3">
              <a
                href="#courses"
                className="block w-full py-3 bg-sage-600 text-white rounded-lg font-medium hover:bg-sage-700 transition-colors"
              >
                Start The Course
              </a>
              <a
                href={SKOOL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-3 border-2 border-sage-600 text-sage-600 rounded-lg font-medium hover:bg-sage-50 transition-colors"
              >
                Join the Community
              </a>
            </div>
          </>
        )}
        
        {status === 'pending' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-serif text-stone-800 mb-4">Payment Processing</h1>
            <p className="text-stone-600">Please wait while we confirm your payment...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-serif text-stone-800 mb-4">Something Went Wrong</h1>
            <p className="text-stone-600 mb-6">Please contact support if you were charged.</p>
            <a href="#membership" className="text-sage-600 hover:underline">
              Return to Membership
            </a>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default MembershipPage;
